/**
 * The give box's relationship with the page around it (GH-847): where it is, who framed it, and
 * the only two things it ever tells that page.
 *
 * ## postMessage, outbound
 *
 * The box posts exactly `embedResizeMessage` and `embedCompleteMessage` (`@givehere/contracts`) —
 * a height, and a status with an amount. Neither carries anything about the donor. The target
 * origin is the framing page's origin when the browser tells us (`location.ancestorOrigins`, else
 * the referrer's origin), and `"*"` only when it does not — which is safe *because* the payloads
 * hold nothing a stranger could use: the worst a wrong recipient learns is a box's height or that
 * a gift of $X was made, which the page showing the box could see on screen anyway.
 *
 * ## postMessage, inbound
 *
 * The box accepts **no** message from the host page: there is no command a host can send it, so
 * there is nothing for a hostile host to forge. The one inbound channel is the Apple Pay window's
 * reply (`EmbedWallet`), accepted only from this box's own origin **and** from the window this box
 * opened (`event.source`), checked by the caller.
 */
import {
  type MoneyWire,
  embedCompleteMessage,
  embedResizeMessage,
  embedWidgetLayoutMessage,
  type EmbedHostMessage,
} from "@givehere/contracts";

const EMBED_ROUTE = /^\/embed\/give\/([^/]+)(\/wallet|\/sign-in)?\/?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `/embed/give/<organizationId>[/wallet|/sign-in]`, or null for any other path or a malformed id.
 * `/sign-in` is GH-877's sign-in window.
 */
export function embedRouteFor(pathname: string): {
  readonly organizationId: string;
  readonly wallet: boolean;
  readonly signIn: boolean;
} | null {
  const match = EMBED_ROUTE.exec(pathname);
  if (!match?.[1]) return null;
  let organizationId: string;
  try {
    organizationId = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  if (!UUID.test(organizationId)) return null;
  return {
    organizationId: organizationId.toLowerCase(),
    wallet: match[2] === "/wallet",
    signIn: match[2] === "/sign-in",
  };
}

/** Whether this page is inside a frame. A frame we cannot even ask about counts as framed. */
export function isFramed(view: Window = window): boolean {
  try {
    return view.self !== view.top;
  } catch {
    return true;
  }
}

/**
 * The origin of the page framing this one, as the browser reports it — `ancestorOrigins` where
 * the engine has it (Chromium, WebKit), else the referrer's origin — or null when not framed or
 * not told. Used only as the target of the box's two host messages, never as a control: any site
 * may frame the box (GH-850).
 */
export function ancestorOrigin(view: Window = window): string | null {
  if (!isFramed(view)) return null;
  const ancestors = (view.location as Location & { ancestorOrigins?: DOMStringList })
    .ancestorOrigins;
  const first = ancestors && ancestors.length > 0 ? ancestors.item(0) : null;
  if (first && first !== "null") return first;
  try {
    const referrer = view.document.referrer;
    return referrer ? new URL(referrer).origin : null;
  } catch {
    return null;
  }
}

/** Post one of the two permitted messages to the framing page. Does nothing when not framed. */
export function postToHost(message: EmbedHostMessage, view: Window = window): void {
  if (!isFramed(view)) return;
  const target = ancestorOrigin(view) ?? "*";
  try {
    view.parent.postMessage(message, target);
  } catch {
    // A parent that navigated away mid-gift: there is no one left to tell.
  }
}

export const postHeight = (height: number, view: Window = window): void =>
  postToHost(embedResizeMessage(height), view);

export const postCompletion = (
  status: "received" | "processing",
  amount: { readonly amountMinor: string; readonly currency: string },
  view: Window = window,
): void => {
  // Only to a framing origin the browser named, never `"*"`: harmless as the payload is, a
  // gift's amount has no reason to reach a window we cannot name.
  if (ancestorOrigin(view) === null) return;
  postToHost(embedCompleteMessage(status, amount), view);
};

/**
 * GH-851: tell the loader how the saved widget is drawn (inline or a popup button, and how wide),
 * so an edit in the console reaches pages already pasted. Presentation only; `"*"` is acceptable
 * where the framer is unnamed because the config is what the box shows anyone.
 */
export const postWidgetLayout = (
  layout: {
    readonly mode: "inline" | "popup";
    readonly size: "compact" | "standard" | "wide" | "full";
    readonly buttonLabel: string;
  },
  view: Window = window,
): void => postToHost(embedWidgetLayoutMessage(layout), view);

/** GH-851: Escape inside a popup's box — the loader closes its dialog and returns focus. */
export const postEscape = (view: Window = window): void =>
  postToHost({ type: "givehere:escape" }, view);

/**
 * The console's draft preview (GH-851), read from the address's fragment: `#preview=<token>` for a
 * signed draft, `#preview` for a saved widget shown read-only. The fragment never reaches a server
 * or a referrer, and a preview can only take capability away (it disables every payment button).
 */
export function previewFromHash(hash: string): {
  readonly preview: boolean;
  readonly token: string | null;
} {
  const text = hash.startsWith("#") ? hash.slice(1) : hash;
  if (text === "preview") return { preview: true, token: null };
  if (!text.startsWith("preview=")) return { preview: false, token: null };
  const token = text.slice("preview=".length);
  return /^[A-Za-z0-9_\-.]{1,8000}$/.test(token)
    ? { preview: true, token }
    : { preview: true, token: null };
}

/* ------------------------------------------------- the Apple Pay window's channel */

/** The wallet window, asking the box that opened it for the gift it is to authorize. */
export type WalletReadyMessage = { readonly type: "givehere:wallet-ready" };
/** The box's answer: the context to quote against and the donor's choices. Same origin only. */
export type WalletGiftMessage = {
  readonly type: "givehere:wallet-gift";
  readonly contextToken: string;
  readonly amountMinor: string;
  readonly costSupport: boolean;
  readonly designationId: string | null;
};
/** The wallet window's outcome, for the box to show and to report (without PII) to its host. */
export type WalletResultMessage = {
  readonly type: "givehere:wallet-result";
  readonly donationId: string;
  readonly accessToken: string;
  readonly total: MoneyWire;
};

/** Whether a message really comes from `expected` on this page's own origin. */
export function fromOwnWindow(
  event: MessageEvent,
  expected: Window | null,
  view: Window = window,
): boolean {
  return expected !== null && event.origin === view.location.origin && event.source === expected;
}

/* --------------------------------------------- the sign-in window's channel (GH-877) */

/**
 * The sign-in window, handing the donor's session back to the box that opened it. Same origin
 * only, to the opener only, and only with the nonce the box put in the window's address — so a
 * page that opened the window itself (another origin) never receives it, and a box accepts only
 * the answer to its own press.
 */
export type SignedInMessage = {
  readonly type: "givehere:embed-signed-in";
  readonly nonce: string;
  readonly token: string;
};

const NONCE = /^[A-Za-z0-9_-]{16,64}$/;

/** The nonce in the sign-in window's fragment (`#n=<nonce>`), or null. */
export function signInNonceFromHash(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get("n");
  return value && NONCE.test(value) ? value : null;
}

/** Whether `data` is the sign-in window's answer for `nonce`. */
export function isSignedInMessage(data: unknown, nonce: string): data is SignedInMessage {
  if (typeof data !== "object" || data === null) return false;
  const message = data as Record<string, unknown>;
  const token = message["token"];
  return (
    message["type"] === "givehere:embed-signed-in" &&
    message["nonce"] === nonce &&
    typeof token === "string" &&
    token.length > 0 &&
    token.length <= 4096
  );
}
